/**
 * StylizedTextCalloutRenderer - Renders a stylized text callout block
 * Requirements: 2.1, 2.2, 2.3, 2.4, 6.1
 *
 * Pure presentation component that renders text content in a gray callout card
 * with rounded corners. Uses TipTap JSONContent for rich text rendering.
 */

import React, { useMemo } from 'react';
import type { JSONContent } from '@tiptap/core';
import { useTheme, type TextCalloutTextSize } from '@course-authoring/shared-types';

import { useCardBackground } from './SectionSurfaceContext';
import { useIsMobileViewport } from './hooks/useIsMobileViewport';
import {
  createRichTextContentResetStyles,
  createRichTextRendererExtensions,
  safeGenerateRichTextHTML,
} from './richTextRenderer';
import { createTextCalloutStyles } from './styles/textCallout';
import { RichTextContent } from './RichTextContent';

export interface StylizedTextCalloutRendererProps {
  content: JSONContent;
  textSize?: TextCalloutTextSize;
  className?: string;
  style?: React.CSSProperties;
}

const contentResetStyles = createRichTextContentResetStyles(
  'stc-content',
  '0.15em'
);

/**
 * StylizedTextCalloutRenderer component
 *
 * Features:
 * - AA-33 callout card sizing and padding from shared theme tokens
 * - Responsive 80% / 90% content width behavior
 * - Renders TipTap JSONContent with inline formatting and list support
 * - No badge or label element
 */
export function StylizedTextCalloutRenderer({
  content,
  textSize = 'standard',
  className,
  style,
}: StylizedTextCalloutRendererProps): React.ReactElement {
  const { tokens } = useTheme();
  const isMobile = useIsMobileViewport(tokens.layout.mobileBreakpoint);
  const isSmallMobile = useIsMobileViewport(tokens.layout.smallMobileBreakpoint);
  const cardBackground = useCardBackground();
  const extensions = useMemo(() => createRichTextRendererExtensions(), []);
  const html = useMemo(() => safeGenerateRichTextHTML(content, extensions), [content, extensions]);
  const textCalloutStyles = useMemo(
    () => createTextCalloutStyles(tokens, isMobile, isSmallMobile, textSize),
    [tokens, isMobile, isSmallMobile, textSize]
  );

  return (
    <div
      className={className}
      style={{ ...textCalloutStyles.card, backgroundColor: cardBackground, ...style }}
    >
      <style>{contentResetStyles}</style>
      <RichTextContent
        className="stc-content"
        style={textCalloutStyles.content}
        html={html}
      />
    </div>
  );
}

StylizedTextCalloutRenderer.displayName = 'StylizedTextCalloutRenderer';
